import type { FC } from 'react';
import React from 'react';
import type { FilterState } from '../components/FilterPanel';
import PrivateBankingTable from './PrivateBankingTable';
import '../PrivateBankingReport.css';

const PAGE_SKELETON_FILTERS: FilterState = {
  showComments: false,
  qualification: 'all',
  activeClientDomicile: true,
  activeCrossBorder: true,
  activeBankDomicile: true,
  passiveClientDomicile: true,
  passiveCrossBorder: true,
  passiveBankDomicile: true,
};

const PAGE_SKELETON_GROUPS = 3;
const PAGE_SKELETON_ROWS = 3;
const PAGE_ACTIVE_COLUMNS = 3;
const PAGE_PASSIVE_COLUMNS = 3;
const PAGE_TOTAL_COLUMNS = 2 + PAGE_ACTIVE_COLUMNS + PAGE_PASSIVE_COLUMNS;

interface PrivateBankingSkeletonProps {
  totalDataColumns: number;
  filters: FilterState;
  groupsCount: number;
  rowsCount: number;
}

const TableColGroup: FC<{ filters: FilterState }> = ({ filters }) => (
  <colgroup>
    <col className="filter-header-cell" style={{ width: '250px' }} />
    <col className="qualification-col" style={{ width: '100px' }} />
    {filters.activeClientDomicile && (
      <col className="indicator-cell" style={{ width: '70px' }} />
    )}
    {filters.activeCrossBorder && (
      <col className="indicator-cell" style={{ width: '70px' }} />
    )}
    {filters.activeBankDomicile && (
      <col className="indicator-cell" style={{ width: '70px' }} />
    )}
    {filters.passiveClientDomicile && (
      <col className="indicator-cell" style={{ width: '70px' }} />
    )}
    {filters.passiveCrossBorder && (
      <col className="indicator-cell" style={{ width: '70px' }} />
    )}
    {filters.passiveBankDomicile && (
      <col className="indicator-cell" style={{ width: '70px' }} />
    )}
    {filters.showComments && (
      <col className="comment-header-cell" style={{ width: '300px' }} />
    )}
  </colgroup>
);

const PrivateBankingSkeleton: FC<PrivateBankingSkeletonProps> = ({
  totalDataColumns,
  filters,
  groupsCount,
  rowsCount,
}) => {
  const skeletonGroups = Array.from(
    { length: groupsCount },
    (_ , groupIndex) => (
      <React.Fragment key={`skeleton-group-${groupIndex}`}>
        <tr>
          <td colSpan={totalDataColumns} style={{ height: '30px' }}></td>
        </tr>
        {/* Group Header Skeleton - plain heading, not collapsible */}
        <tr className="group-section-header-row">
          <td
            colSpan={totalDataColumns}
            className="group-section-header"
          >
            <div
              className="skeleton-text"
              style={{
                width: '200px',
                height: '20px',
                display: 'inline-block',
              }}
            ></div>
          </td>
        </tr>
        {/* SubGroup Card Skeleton */}
        <tr>
          <td colSpan={totalDataColumns} className="subgroup-card-cell">
            <div className="subgroup-card">
              <div className="subgroup-card-header">
                <div
                  className="skeleton-text"
                  style={{
                    width: '150px',
                    height: '18px',
                    display: 'inline-block',
                  }}
                ></div>
              </div>
              <table
                className="table private-banking-table private-banking-table-no-border"
                style={{
                  width: '100%',
                  borderCollapse: 'separate',
                  borderSpacing: 0,
                }}
              >
                <TableColGroup filters={filters} />
                <tbody>
                  {/* Data Rows Skeleton */}
                  {Array.from({ length: rowsCount }, (_ , rowIndex) => (
                    <tr
                      key={`skeleton-row-${rowIndex}`}
                      className="data-row"
                    >
                      <td className="title-cell">
                        <div
                          className="skeleton-text"
                          style={{
                            width: '80%',
                            height: '16px',
                            marginBottom: '8px',
                          }}
                        ></div>
                        <div
                          className="skeleton-text"
                          style={{ width: '60%', height: '12px' }}
                        ></div>
                      </td>
                      <td className="qualification-cell text-end">
                        <div
                          className="skeleton-text"
                          style={{
                            width: '70px',
                            height: '16px',
                            marginLeft: 'auto',
                          }}
                        ></div>
                      </td>
                      {filters.activeClientDomicile && (
                        <td className="indicator-cell">
                          <div className="indicator-cell-wrapper">
                            <div className="skeleton-icon"></div>
                          </div>
                        </td>
                      )}
                      {filters.activeCrossBorder && (
                        <td className="indicator-cell">
                          <div className="indicator-cell-wrapper">
                            <div className="skeleton-icon"></div>
                          </div>
                        </td>
                      )}
                      {filters.activeBankDomicile && (
                        <td className="indicator-cell">
                          <div className="indicator-cell-wrapper">
                            <div className="skeleton-icon"></div>
                          </div>
                        </td>
                      )}
                      {filters.passiveClientDomicile && (
                        <td className="indicator-cell">
                          <div className="indicator-cell-wrapper">
                            <div className="skeleton-icon"></div>
                          </div>
                        </td>
                      )}
                      {filters.passiveCrossBorder && (
                        <td className="indicator-cell">
                          <div className="indicator-cell-wrapper">
                            <div className="skeleton-icon"></div>
                          </div>
                        </td>
                      )}
                      {filters.passiveBankDomicile && (
                        <td className="indicator-cell">
                          <div className="indicator-cell-wrapper">
                            <div className="skeleton-icon"></div>
                          </div>
                        </td>
                      )}
                      {filters.showComments && (
                        <td className="comment-cell">
                          <div
                            className="skeleton-text"
                            style={{ width: '90%', height: '14px' }}
                          ></div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </td>
        </tr>
      </React.Fragment>
    )
  );

  return <tbody>{skeletonGroups}</tbody>;
};

/**
 * Full Private Banking page chrome + table skeleton.
 * Safe as a Suspense fallback (imports its own CSS) and during eurl decrypt.
 */
export const PrivateBankingPageSkeleton: FC = () => (
  <div className="ao_rowBg v3-pb-report">
    <div className="ao_mainContainer container-fluid">
      <div className="d-flex flex-column d-sm-block mb-4">
        <div className="float-sm-end ao_secondaryBtnGroup order-3 mt-0 mb-2">
          <span className="skeleton-icon" style={{ width: 110, height: 36 }} />
          <span className="skeleton-icon" style={{ width: 100, height: 36 }} />
        </div>
        <h1 className="Garamond fw-semibold v3-heading">
          <span
            className="skeleton-text"
            style={{ width: '220px', height: '32px' }}
          />
        </h1>
        <p className="ao_showSource mt-2 mb-1">
          <span
            className="skeleton-text"
            style={{ width: '260px', height: '14px' }}
          />
        </p>
      </div>
      <PrivateBankingTable
        filters={PAGE_SKELETON_FILTERS}
        activeColumnsCount={PAGE_ACTIVE_COLUMNS}
        passiveColumnsCount={PAGE_PASSIVE_COLUMNS}
        totalDataColumns={PAGE_TOTAL_COLUMNS}
        isLoading
        renderSkeletonLoader={() => (
          <PrivateBankingSkeleton
            totalDataColumns={PAGE_TOTAL_COLUMNS}
            filters={PAGE_SKELETON_FILTERS}
            groupsCount={PAGE_SKELETON_GROUPS}
            rowsCount={PAGE_SKELETON_ROWS}
          />
        )}
        groupedData={[]}
        expandedSections={new Set()}
        toggleSection={() => {}}
        processRowsForMerging={() => []}
        renderRow={() => null}
        setShowFilterPanel={() => {}}
      />
    </div>
  </div>
);

export default PrivateBankingSkeleton;

